import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { displayPrice, useUsdTry } from "../../lib/price";

export const PackageCard = ({ pkg, featured }) => {
  const { t, tc, lang } = useLang();
  const rate = useUsdTry();
  const priceFrom = displayPrice(pkg.price_from, pkg.price_from_try, lang, rate);
  const name = tc(pkg.name);
  const benefits = tc(pkg.investment_benefits) || [];
  const isFeatured = featured ?? pkg.featured;

  return (
    <div
      data-testid={`package-card-${pkg.slug}`}
      className={`relative rounded-xl sm:rounded-3xl p-3 sm:p-8 lg:p-10 h-full flex flex-col transition-all duration-500 ${
        isFeatured
          ? "bg-charcoal text-white shadow-[0_30px_70px_rgba(0,0,0,0.25)] lg:scale-[1.04] z-10 grain"
          : "bg-white text-charcoal border border-border hover:border-gold/40 hover:shadow-xl"
      }`}
    >
      {isFeatured && (
        <span className="absolute -top-2.5 sm:-top-3.5 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 bg-gold text-charcoal text-[8px] sm:text-[11px] font-bold tracking-wide px-2 sm:px-4 py-1 sm:py-1.5 rounded-full whitespace-nowrap">
          {t.packages.mostPopular}
        </span>
      )}
      <span className="overline text-[9px] sm:text-xs">0{pkg.order}</span>
      <h3 className={`font-display text-sm sm:text-2xl lg:text-3xl mt-1.5 sm:mt-3 mb-2 sm:mb-3 leading-tight ${isFeatured ? "text-white" : "text-charcoal"}`}>{name}</h3>
      <p className={`text-[11px] sm:text-sm leading-relaxed mb-3 sm:mb-6 line-clamp-4 sm:line-clamp-none ${isFeatured ? "text-white/70" : "text-charcoal/55"}`}>{tc(pkg.overview)}</p>

      <div className="mb-3 sm:mb-6">
        <span className={`text-[9px] sm:text-xs ${isFeatured ? "text-white/50" : "text-charcoal/40"}`}>{t.packages.from}</span>
        <div className={`font-display text-base sm:text-3xl lg:text-4xl leading-tight ${isFeatured ? "text-gold" : "text-green-deep"}`}>{priceFrom}</div>
      </div>

      <ul className="space-y-1.5 sm:space-y-3 mb-4 sm:mb-8 flex-1">
        {(Array.isArray(benefits) ? benefits : []).slice(0, 5).map((b, i) => (
          <li key={i} className={`items-start gap-1.5 sm:gap-3 text-[10px] sm:text-sm leading-snug ${i > 2 ? "hidden sm:flex" : "flex"} ${isFeatured ? "text-white/80" : "text-charcoal/70"}`}>
            <Check className={`w-3 h-3 sm:w-4 sm:h-4 mt-0.5 shrink-0 ${isFeatured ? "text-gold" : "text-green-deep"}`} />
            {b}
          </li>
        ))}
      </ul>

      <Link
        to="/contact"
        data-testid={`package-cta-${pkg.slug}`}
        className={`group inline-flex items-center justify-center gap-1 sm:gap-2 rounded-full font-semibold text-[10px] sm:text-base px-2 sm:px-6 py-2 sm:py-3.5 text-center leading-tight transition-colors ${
          isFeatured ? "bg-gold text-charcoal hover:bg-gold-soft" : "bg-green text-white hover:bg-green-deep"
        }`}
      >
        {t.packages.choose}
        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 shrink-0 group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );
};
